import { useNavigate, useParams } from "react-router-dom";
import { deleteHabit, viewHabitDetails } from "../../services/habitServices";
import { useEffect, useState } from "react";
import { LoadingSpinner } from "../../components/LoadingSpinner";

import {
    ArrowLeftIcon,
    TrashIcon,
    PlusCircleIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    DocumentTextIcon,
    FireIcon,
} from "@heroicons/react/24/outline";

import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { formatDate, formatDateTime } from "../../utils/date";

export default function HabitDetail() {
    const { id } = useParams();
    const [habit, setHabit] = useState(null);
    const [loading, setLoading] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        const fetchHabitDetails = async () => {
            setLoading(true);
            try {
                const data = await viewHabitDetails(id);
                setHabit(data);
            } finally {
                setLoading(false);
            }
        };
        fetchHabitDetails();
    }, [id]);

    const handleDeleteHabit = () => {
        Swal.fire({
            title: "Bạn có chắc chắn?",
            text: "Thói quen sẽ bị xoá vĩnh viễn!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Xoá",
            cancelButtonText: "Hủy",
            confirmButtonColor: "#e11d48",
        }).then(async (res) => {
            if (!res.isConfirmed) return;

            const deleting = deleteHabit(id);
            toast.promise(deleting, {
                loading: "Đang xoá thói quen...",
                success: "Đã xoá thói quen!",
                error: "Không thể xoá thói quen!",
            });

            try {
                await deleting;
                nav(-1);
            } catch { }
        });
    };

    if (loading) return <LoadingSpinner content="Đang tải chi tiết thói quen..." />;
    if (!habit) return <p>Không tìm thấy thói quen.</p>;

    /* ===== CHỈ XOÁ TRONG 3 NGÀY ===== */
    const createdDate = new Date(habit.created_at);
    const now = new Date();
    const diffDays = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
    const canDelete = diffDays <= 3;

    /* ===== KIỂM TRA HẾT HẠN ===== */
    const today = new Date().toISOString().split("T")[0];
    const isOverdue = habit.due_date && habit.due_date < today;

    return (
        <div className="max-w-5xl mx-auto p-6 space-y-6">

            {/* HEADER */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => nav(-1)}
                    className="p-2 bg-white border rounded-full shadow hover:bg-gray-50 transition"
                >
                    <ArrowLeftIcon className="w-6 h-6 text-gray-700" />
                </button>
                <h1 className="text-3xl font-bold text-gray-900">
                    Chi tiết thói quen
                </h1>
            </div>

            {/* HABIT INFO */}
            <div className="bg-white shadow-lg border border-gray-200 rounded-xl p-5 space-y-4">
                <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                    <CheckCircleIcon className="w-7 h-7 text-blue-600" />
                    {habit.title}
                </h2>

                <p className="text-gray-600">{habit.description}</p>
                <p className="text-sm text-gray-500">
                    Thời gian tạo: {formatDateTime(habit.created_at)}
                </p>

                <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                    <p className="flex items-center gap-1">
                        <CalendarDaysIcon className="w-5 h-5 text-blue-600" />
                        Bắt đầu:
                        <span className="font-semibold">
                            {formatDate(habit.start_date)}
                        </span>
                    </p>

                    <p className="flex items-center gap-1">
                        <CalendarDaysIcon className="w-5 h-5 text-blue-600" />
                        Kết thúc:
                        <span className="font-semibold">
                            {habit.due_date ? formatDate(habit.due_date) : "Chưa đặt"}
                        </span>
                    </p>

                    <p className="flex items-center gap-2 text-orange-600 font-semibold">
                        <FireIcon className="w-6 h-6" />
                        Chuỗi duy trì:
                        <span className="text-gray-800">{habit.streak} ngày</span>
                    </p>
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex gap-4 pt-4">

                    {/* ===== NÚT XOÁ + TOOLTIP ===== */}
                    <div className="relative group">
                        <button
                            onClick={() => canDelete && handleDeleteHabit()}
                            disabled={!canDelete}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition
                                ${canDelete
                                    ? "bg-red-500 text-white hover:bg-red-600"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                }`}
                        >
                            <TrashIcon className="w-5 h-5" />
                            {canDelete ? "Xoá thói quen" : "Hết thời gian xoá"}
                        </button>

                        {!canDelete && (
                            <div
                                className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                                           w-64 text-center text-sm text-white
                                           bg-gray-900 px-3 py-2 rounded-lg
                                           opacity-0 group-hover:opacity-100
                                           transition pointer-events-none z-50"
                            >
                                Chỉ có thể xoá thói quen trong vòng <b>3 ngày</b> kể từ khi tạo
                            </div>
                        )}
                    </div>

                    {/* ADD LOG */}
                    <button
                        onClick={() =>
                            !isOverdue &&
                            nav("/dashboard/habit/log/create", { state: { habit } })
                        }
                        disabled={isOverdue}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow transition
                            ${isOverdue
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-green-500 text-white hover:bg-green-600"
                            }`}
                    >
                        <PlusCircleIcon className="w-5 h-5" />
                        {isOverdue ? "Đã hết hạn" : "Thêm nhật ký mới"}
                    </button>
                </div>
            </div>

            {/* LOGS */}
            <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                    Nhật ký theo ngày
                </h3>

                {habit.logs.length === 0 && (
                    <p className="text-gray-500 italic">Chưa có nhật ký nào.</p>
                )}

                {habit.logs.map((log) => (
                    <div
                        key={log.id}
                        className="bg-white border border-gray-200 shadow-md rounded-xl p-5 space-y-3"
                    >
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                            <CalendarDaysIcon className="w-5 h-5 text-gray-500" />
                            {new Date(log.date).toLocaleDateString("vi-VN")}
                        </p>

                        <div
                            className="prose max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: log.notes }}
                        />

                        {log.photo && (
                            <img
                                src={log.photo}
                                alt="Ảnh nhật ký"
                                className="rounded-lg shadow border max-w-sm"
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
